import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';

const Header = ({ onMenuToggle, isMobileMenuOpen = false }) => {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });
  const location = useLocation();
  const { user, signOut } = useAuth();

  useEffect(() => {
    // Apply theme to document properly
    const applyTheme = (newTheme) => {
      const root = document.documentElement;
      
      // Remove existing theme classes
      root.classList?.remove('light', 'dark');
      
      // Add new theme class
      if (newTheme === 'light') {
        root.classList?.add('light');
      }
      // Dark is default, no class needed due to CSS variables
      
      // Save to localStorage
      localStorage.setItem('theme', newTheme);
      
      console.log('🎨 Tema alterado para:', newTheme);
    };

    applyTheme(theme);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const navigationItems = [
    { label: 'Dashboard', path: '/dashboard-overview', icon: 'LayoutDashboard' },
    { label: 'Impressoras 3D', path: '/printer-management', icon: 'Printer' },
    { label: 'Filamentos', path: '/filament-inventory', icon: 'Filament' },
    { label: 'Calculadora', path: '/calculator', icon: 'Calculator' },
    { label: 'Cálculo Rápido', path: '/quick-calculator', icon: 'Zap' },
    { label: 'Histórico', path: '/historico', icon: 'Projects' },
    { label: 'Configurações', path: '/configuracoes', icon: 'Settings' }
  ];

  const isActivePath = (path) => location?.pathname === path;

  return (
    <header className="glass-header fixed top-0 left-0 right-0 h-16 z-60 lg:h-16 sm:h-14 transition-all duration-300">
      <div className="flex items-center justify-between h-full px-6 lg:px-6 sm:px-4">
        {/* Logo Section */}
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuToggle}
            className="lg:hidden mr-3 hover:bg-muted transition-colors"
            aria-label="Alternar menu"
          >
            <Icon 
              name={isMobileMenuOpen ? "X" : "Menu"} 
              size={20} 
              className="text-foreground"
            />
          </Button>
          
          <Link 
            to="/dashboard-overview" 
            className="flex items-center space-x-3 hover:opacity-90 transition-opacity group"
            aria-label="Ir para Dashboard"
          >
            <img
              src="/filament.svg"
              alt="FioGo"
              className="h-8 w-auto sm:h-7 drop-shadow-md group-hover:scale-[1.02] transition-transform"
              loading="eager"
              decoding="async"
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        {user && (
          <nav className="hidden lg:flex items-center space-x-2">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`nav-item ${isActivePath(item?.path) ? 'nav-item-active' : ''}`}
                aria-current={isActivePath(item?.path) ? 'page' : undefined}
              >
                <Icon 
                  name={item?.icon} 
                  size={16} 
                  variant={['Printer','Filament','Projects'].includes(item?.icon) ? 'masked' : 'lucide'}
                  className={isActivePath(item?.path) ? 'text-primary-foreground' : 'text-muted-foreground'}
                />
                <span>{item?.label}</span>
              </Link>
            ))}
          </nav>
        )}

        {/* Right Section */}
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="hover:bg-muted transition-colors relative overflow-hidden"
            aria-label="Alternar tema"
          >
            <div className="relative w-5 h-5">
              {/* Sun Icon */}
              <Icon 
                name="Sun" 
                size={18} 
                className={`absolute inset-0 text-muted-foreground hover:text-foreground transition-all duration-300 ${
                  theme === 'light' ? 'opacity-100 rotate-0' : 'opacity-0 rotate-90'
                }`}
              />
              {/* Moon Icon */}
              <Icon 
                name="Moon" 
                size={18} 
                className={`absolute inset-0 text-muted-foreground hover:text-foreground transition-all duration-300 ${
                  theme === 'dark' ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'
                }`}
              />
            </div>
          </Button>
          
          {user ? (
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center cursor-pointer hover:scale-105 transition-transform shadow-lg">
                <Icon name="User" size={16} className="text-white" />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Sair
              </Button>
            </div>
          ) : (
            <Link to="/authentication-login">
              <Button variant="default" size="sm">
                Entrar
              </Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;